import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Plus, 
  Trash2,
  ArrowLeft,
  Download,
  Calendar,
  Loader2,
  Building,
  CreditCard,
  Users
} from 'lucide-react';

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'USD' },
  { code: 'EUR', symbol: '€', name: 'EUR' },
  { code: 'GBP', symbol: '£', name: 'GBP' },
  { code: 'PLN', symbol: 'zł', name: 'PLN' }
];

function numberToWordsEN(num) {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  
  if (num === 0) return 'zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' and ' + numberToWordsEN(num % 100) : '');
  if (num < 1000000) return numberToWordsEN(Math.floor(num / 1000)) + ' thousand' + (num % 1000 ? ' ' + numberToWordsEN(num % 1000) : '');
  return num.toString();
}

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function PocketbookWisePage() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Form data - Provider is selectable, Customer is fixed (Pocketbook)
  const [formData, setFormData] = useState({
    // Invoice info
    invoice_number: `${currentYear} ${currentMonth + 1} 1`,
    invoice_date: currentDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.'),
    month_index: currentMonth,
    year: currentYear,
    
    // Provider info (selectable from list)
    provider_name: '',
    provider_tax_id: '',
    provider_country: 'United Kingdom',
    provider_address: '',
    
    // Customer info (fixed - Pocketbook International SA)
    customer_name: 'Pocketbook International SA',
    customer_address: 'Crocicchio Cortogna, 6, 6900 Lugano, Switzerland',
    
    // Contract info
    contract_name: 'Master Services Agreement',
    contract_number: '',
    contract_date: '',
    
    // Service description
    service_description: 'Payment for firmware development and testing services',
    
    // Currency
    currency: 'USD',
    
    // Total amount
    total_amount: '',
    
    // Bank info (provider's bank details - Wise style)
    bank_name: '',
    bank_address: '',
    bank_account: '',
    bank_swift: '',
    
    // Tasks (hours per task, no amount per task)
    tasks: [
      { description: '', hours: '' }
    ]
  });

  useEffect(() => {
    fetchProviders();
    
    // Check for imported Redmine tasks
    const redmineTasks = sessionStorage.getItem('redmine_tasks');
    const redmineMonth = sessionStorage.getItem('redmine_month');
    const redmineYear = sessionStorage.getItem('redmine_year');
    
    if (redmineTasks) {
      try {
        const tasks = JSON.parse(redmineTasks);
        // Convert to Wise format (description only, no description_en/ua split)
        const wiseTasks = tasks.map(t => ({
          description: t.description || t.description_en || '',
          hours: t.hours || ''
        }));
        setFormData(prev => ({
          ...prev,
          tasks: wiseTasks,
          month_index: redmineMonth ? parseInt(redmineMonth) : prev.month_index,
          year: redmineYear ? parseInt(redmineYear) : prev.year
        }));
        // Clear the session storage after importing
        sessionStorage.removeItem('redmine_tasks');
        sessionStorage.removeItem('redmine_month');
        sessionStorage.removeItem('redmine_year');
        toast.success(`Imported ${tasks.length} tasks from Redmine`);
      } catch (e) {
        console.error('Failed to parse Redmine tasks:', e);
      }
    }
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await api.get('/clients');
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch providers');
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleProviderSelect = (providerId) => {
    setSelectedProviderId(providerId);
    
    if (providerId === 'new' || !providerId) {
      // Clear provider fields for manual entry
      setFormData(prev => ({
        ...prev,
        provider_name: '',
        provider_tax_id: '',
        provider_country: 'United Kingdom',
        provider_address: '',
        contract_name: 'Master Services Agreement',
        contract_number: '',
        contract_date: '',
        bank_name: '',
        bank_address: '',
        bank_account: '',
        bank_swift: ''
      }));
      return;
    }
    
    const provider = providers.find(p => p.id === providerId);
    if (provider) {
      // Auto-fill all provider fields
      setFormData(prev => ({
        ...prev,
        provider_name: provider.name || '',
        provider_tax_id: provider.tax_id || '',
        provider_address: provider.address_en || provider.address || '',
        contract_name: provider.contract_name_en || prev.contract_name,
        contract_number: provider.contract_number || prev.contract_number,
        contract_date: provider.contract_date || prev.contract_date,
        service_description: provider.service_description_en || prev.service_description,
        currency: provider.currency || prev.currency,
        // Bank details
        bank_name: provider.bank_name || prev.bank_name,
        bank_address: provider.bank_address || prev.bank_address,
        bank_account: provider.bank_account || provider.bank_iban || prev.bank_account,
        bank_swift: provider.bank_swift || prev.bank_swift,
      }));
      
      toast.success(`Provider "${provider.name}" loaded`);
    }
  };

  const handleTaskChange = (index, field, value) => {
    const newTasks = [...formData.tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setFormData({ ...formData, tasks: newTasks });
  };

  const addTask = () => {
    if (formData.tasks.length >= 15) {
      toast.error('Maximum 15 tasks allowed');
      return;
    }
    setFormData({
      ...formData,
      tasks: [...formData.tasks, { description: '', hours: '' }]
    });
  };

  const removeTask = (index) => {
    if (formData.tasks.length === 1) return;
    const newTasks = formData.tasks.filter((_, i) => i !== index);
    setFormData({ ...formData, tasks: newTasks });
  };

  const calculateTotalHours = () => {
    return formData.tasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleGenerate = async (format = 'docx') => {
    const total = parseFloat(formData.total_amount) || 0;
    if (total === 0) {
      toast.error('Please enter the total amount');
      return;
    }

    if (!formData.provider_name) {
      toast.error('Please select or enter a provider');
      return;
    }

    setGenerating(true);
    try {
      const currencyWord = formData.currency === 'USD' ? 'US dollars' : 
                          (formData.currency === 'EUR' ? 'euros' : 
                          (formData.currency === 'GBP' ? 'British pounds' : 'Polish zloty'));
      
      const payload = {
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date,
        month: MONTHS_EN[formData.month_index],
        year: String(formData.year),
        custom_fields: {
          'MONTH_EN': MONTHS_EN[formData.month_index].toUpperCase(),
          'PROVIDER_NAME': formData.provider_name,
          'PROVIDER_TAX_ID': formData.provider_tax_id,
          'PROVIDER_COUNTRY': formData.provider_country,
          'PROVIDER_ADDRESS': formData.provider_address,
          'CUSTOMER_NAME': formData.customer_name,
          'CUSTOMER_ADDRESS': formData.customer_address,
          'CONTRACT_NAME': formData.contract_name,
          'CONTRACT_NUMBER': formData.contract_number,
          'CONTRACT_DATE': formData.contract_date,
          'SERVICE_DESCRIPTION': formData.service_description,
          'CURRENCY': formData.currency,
          'BANK_NAME': formData.bank_name,
          'BANK_ADDRESS': formData.bank_address,
          'BANK_ACCOUNT': formData.bank_account,
          'BANK_SWIFT': formData.bank_swift,
          'TOTAL_AMOUNT': formatCurrency(total),
          'TOTAL_WORDS_EN': capitalizeFirst(numberToWordsEN(Math.round(total))) + ' ' + currencyWord,
          ...formData.tasks.reduce((acc, task, i) => {
            const num = i + 1;
            acc[`TASK_${num}_DESC`] = task.description || '';
            acc[`TASK_${num}_HOURS`] = task.hours || '';
            return acc;
          }, {})
        }
      };

      const response = await api.post(
        `/invoices/generate-pocketbook-wise?output_format=${format}`,
        payload,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = MONTHS_EN[formData.month_index];
      link.setAttribute('download', `Invoice_${formData.provider_name.replace(/\s+/g, '_')}_${monthName}_${formData.year}_wise.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Invoice downloaded as ${format.toUpperCase()}!`);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pocketbook-wise-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/templates')}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">Pocketbook Wise Invoice</h1>
            <p className="page-subtitle">
              Generate invoice for Pocketbook (English only, Wise bank format)
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Provider Selection */}
          <Card className="dashboard-card border-2 border-emerald-500/20 bg-emerald-500/5">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                Select Provider (Service Provider)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SearchableSelect
                options={[{ id: 'new', name: '+ Enter manually' }, ...providers.map(p => ({
                  ...p,
                  name: p.name,
                  contract_number: p.contract_number || ''
                }))]}
                value={selectedProviderId}
                onValueChange={handleProviderSelect}
                placeholder="Search and select a provider..."
                searchPlaceholder="Search by name or contract number..."
                emptyMessage="No providers found"
                disabled={loadingProviders}
                displayKey="name"
                valueKey="id"
                secondaryKey="contract_number"
              />
              {providers.length === 0 && !loadingProviders && (
                <p className="text-sm text-slate-500 mt-2">
                  No providers yet. <a href="/clients" className="text-emerald-600 underline">Add providers</a> to quickly generate invoices.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Customer Info (Fixed - Pocketbook) */}
          <Card className="dashboard-card bg-slate-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-slate-600" />
                Customer (Fixed)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="font-semibold text-slate-900">{formData.customer_name}</p>
                <p className="text-sm text-slate-600 mt-1">{formData.customer_address}</p>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Invoice Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="form-group">
                  <Label>Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="font-mono"
                  />
                </div>
                <div className="form-group">
                  <Label>Invoice Date</Label>
                  <Input
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    placeholder="DD.MM.YYYY"
                  />
                </div>
                <div className="form-group">
                  <Label>Month</Label>
                  <Select 
                    value={String(formData.month_index)} 
                    onValueChange={(v) => setFormData({ ...formData, month_index: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS_EN.map((month, i) => (
                        <SelectItem key={i} value={String(i)}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Provider Info */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-emerald-600" />
                Provider Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="form-grid">
                <div className="form-group">
                  <Label>Provider Name</Label>
                  <Input
                    value={formData.provider_name}
                    onChange={(e) => setFormData({ ...formData, provider_name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <Label>Tax ID / Registration Number</Label>
                  <Input
                    value={formData.provider_tax_id}
                    onChange={(e) => setFormData({ ...formData, provider_tax_id: e.target.value })}
                    placeholder="Registration number"
                    className="font-mono"
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label>Country</Label>
                  <Input
                    value={formData.provider_country}
                    onChange={(e) => setFormData({ ...formData, provider_country: e.target.value })}
                    placeholder="United Kingdom"
                  />
                </div>
                <div className="form-group">
                  <Label>Address</Label>
                  <Input
                    value={formData.provider_address}
                    onChange={(e) => setFormData({ ...formData, provider_address: e.target.value })}
                    placeholder="Full address"
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label>Contract Name</Label>
                  <Input
                    value={formData.contract_name}
                    onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })}
                    placeholder="Master Services Agreement"
                  />
                </div>
                <div className="form-group">
                  <Label>Contract Number</Label>
                  <Input
                    value={formData.contract_number}
                    onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                    placeholder="2801/21"
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label>Contract Date</Label>
                  <Input
                    value={formData.contract_date}
                    onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                    placeholder="28.01.2021"
                  />
                </div>
                <div className="form-group">
                  <Label>Service Description</Label>
                  <Input
                    value={formData.service_description}
                    onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
                    placeholder="Payment for services..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Tasks / Services</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addTask}>
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.tasks.map((task, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">Task {index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTask(index)}
                        className="h-8 w-8 p-0 hover:bg-rose-50"
                        disabled={formData.tasks.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                    <div className="form-group">
                      <Label>Description</Label>
                      <Textarea
                        value={task.description}
                        onChange={(e) => handleTaskChange(index, 'description', e.target.value)}
                        placeholder="Firmware development and testing..."
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <Label>Hours</Label>
                      <Input
                        type="number"
                        value={task.hours}
                        onChange={(e) => handleTaskChange(index, 'hours', e.target.value)}
                        className="w-32"
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Total Amount Section */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-base font-semibold">Total Amount</Label>
                    <p className="text-sm text-slate-500">Enter the total amount for all tasks</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select 
                      value={formData.currency} 
                      onValueChange={(v) => setFormData({ ...formData, currency: v })}
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} {curr.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                      placeholder="0"
                      className="w-40 text-lg font-semibold"
                    />
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Total hours: {calculateTotalHours()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Info */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Bank Information (Wise)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="form-grid">
                <div className="form-group">
                  <Label>Beneficiary's Bank</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="COMMUNITY FEDERAL SAVINGS BANK"
                  />
                </div>
                <div className="form-group">
                  <Label>SWIFT Code</Label>
                  <Input
                    value={formData.bank_swift}
                    onChange={(e) => setFormData({ ...formData, bank_swift: e.target.value })}
                    className="font-mono"
                    placeholder="CMFGUS33"
                  />
                </div>
              </div>
              <div className="form-group">
                <Label>Bank Address</Label>
                <Input
                  value={formData.bank_address}
                  onChange={(e) => setFormData({ ...formData, bank_address: e.target.value })}
                  placeholder="30 W. 26th Street, Sixth Floor New York NY 10010 United States"
                />
              </div>
              <div className="form-group">
                <Label>Account Number</Label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  className="font-mono"
                  placeholder="8312144640"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary & Download */}
        <div className="space-y-6">
          <Card className="dashboard-card sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-600" />
                Generate Invoice
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Invoice #</span>
                  <span className="font-mono font-medium">{formData.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Provider</span>
                  <span className="font-medium truncate max-w-[150px]">{formData.provider_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Customer</span>
                  <span className="font-medium truncate max-w-[150px]">Pocketbook Int.</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Period</span>
                  <span className="font-medium">{MONTHS_EN[formData.month_index]} {formData.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tasks</span>
                  <span className="font-medium">{formData.tasks.filter(t => t.description).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Hours</span>
                  <span className="font-medium">{calculateTotalHours()}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">Total</span>
                  <span className="text-xl font-bold text-emerald-600">
                    {CURRENCIES.find(c => c.code === formData.currency)?.symbol}{formatCurrency(formData.total_amount)} {formData.currency}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => handleGenerate('docx')}
                  disabled={generating}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="generate-docx-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download DOCX
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => handleGenerate('pdf')}
                  disabled={generating}
                  variant="outline"
                  className="w-full"
                  data-testid="generate-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-sm">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/clients')}
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Providers
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => navigate('/pocketbook-invoice')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Pocketbook UA Invoice
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
